/**
 * Avatar Editor Component
 * Allows users to customize their avatar appearance
 */
import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { X, Shirt, Glasses } from 'lucide-react';

// Avatar customization options
export const SKIN_TONES = [
  { id: 'tone1', name: 'Porcelain', color: '#FFE4C4' },
  { id: 'light', name: 'Light', color: '#FFE0BD' },
  { id: 'medium', name: 'Medium', color: '#D4A574' },
  { id: 'tan', name: 'Tan', color: '#C68642' },
  { id: 'brown', name: 'Brown', color: '#8D5524' },
  { id: 'dark', name: 'Dark', color: '#5C3A21' },
  { id: 'deep', name: 'Deep', color: '#3D2314' },
  { id: 'ebony', name: 'Ebony', color: '#2D1810' },
];

export const FACE_MOODS = [
  { id: 'neutral', name: 'Neutral', emoji: '😐' },
  { id: 'happy', name: 'Happy', emoji: '😊' },
  { id: 'cool', name: 'Cool', emoji: '😎' },
  { id: 'excited', name: 'Hyped', emoji: '🤩' },
  { id: 'wink', name: 'Wink', emoji: '😉' },
  { id: 'party', name: 'Party', emoji: '🥳' },
  { id: 'chill', name: 'Chill', emoji: '😌' },
  { id: 'fierce', name: 'Fierce', emoji: '😤' },
];

// NEW P2: Hair styles
export const HAIR_STYLES = [
  { id: 'none', name: 'None', type: 'none' },
  { id: 'short', name: 'Short', type: 'short' },
  { id: 'buzz', name: 'Buzz Cut', type: 'buzz' },
  { id: 'mohawk', name: 'Mohawk', type: 'mohawk' },
  { id: 'long', name: 'Long', type: 'long' },
  { id: 'ponytail', name: 'Ponytail', type: 'ponytail' },
  { id: 'curly', name: 'Curly', type: 'curly' },
  { id: 'spiky', name: 'Spiky', type: 'spiky' },
];

// NEW P2: Hair colors
export const HAIR_COLORS = [
  { id: 'black', name: 'Black', color: '#1a1a1a' },
  { id: 'brown', name: 'Brown', color: '#4a3728' },
  { id: 'blonde', name: 'Blonde', color: '#d4a76a' },
  { id: 'red', name: 'Red', color: '#8b2500' },
  { id: 'purple', name: 'Purple', color: '#9333EA' },
  { id: 'pink', name: 'Pink', color: '#EC4899' },
  { id: 'blue', name: 'Blue', color: '#3b82f6' },
  { id: 'neon', name: 'Neon', color: '#39FF14', glow: '#39FF14' },
];

export const EYE_COLORS = [
  { id: 'blue', name: 'Blue', color: '#4A90D9' },
  { id: 'green', name: 'Green', color: '#4ADE80' },
  { id: 'brown', name: 'Brown', color: '#8B4513' },
  { id: 'purple', name: 'Purple', color: '#9333EA' },
  { id: 'red', name: 'Red', color: '#DC2626' },
  { id: 'gold', name: 'Gold', color: '#FFD700' },
  { id: 'cyan', name: 'Cyan', color: '#22D3EE' },
  { id: 'pink', name: 'Pink', color: '#EC4899' },
];

export const TOPS = [
  { id: 'tank_black', name: 'Black Tank', color: '#1a1a1a' },
  { id: 'tank_white', name: 'White Tank', color: '#f0f0f0' },
  { id: 'hoodie_purple', name: 'Purple Hoodie', color: '#9333EA' },
  { id: 'hoodie_red', name: 'Red Hoodie', color: '#DC2626' },
  { id: 'tee_neon', name: 'Neon Tee', color: '#39FF14', glow: '#39FF14' },
  { id: 'jacket_leather', name: 'Leather Jacket', color: '#2d2d2d' },
  { id: 'crop_pink', name: 'Pink Crop', color: '#EC4899' },
  { id: 'mesh_black', name: 'Mesh Top', color: '#1a1a1a' },
];

export const PANTS = [
  { id: 'jeans_black', name: 'Black Jeans', color: '#1a1a1a' },
  { id: 'jeans_blue', name: 'Blue Jeans', color: '#1e3a5f' },
  { id: 'cargo_dark', name: 'Dark Cargo', color: '#2d2d2d' },
  { id: 'joggers_gray', name: 'Gray Joggers', color: '#4a4a4a' },
  { id: 'shorts_neon', name: 'Neon Shorts', color: '#39FF14' },
  { id: 'skirt_purple', name: 'Purple Skirt', color: '#9333EA' },
  { id: 'leather_pants', name: 'Leather Pants', color: '#1a1a1a' },
  { id: 'baggy_white', name: 'White Baggy', color: '#e0e0e0' },
];

export const SHOES = [
  { id: 'sneakers_white', name: 'White Sneakers', color: '#f0f0f0' },
  { id: 'sneakers_black', name: 'Black Sneakers', color: '#1a1a1a' },
  { id: 'boots_combat', name: 'Combat Boots', color: '#2d2d2d' },
  { id: 'rave_green', name: 'Rave Kicks', color: '#39FF14', glow: '#39FF14' },
  { id: 'platforms', name: 'Platforms', color: '#9333EA' },
  { id: 'barefoot', name: 'Barefoot', color: 'transparent' },
  { id: 'heels_red', name: 'Red Heels', color: '#DC2626' },
  { id: 'jordans', name: 'Jordans', color: '#DC2626' },
];

export const HATS = [
  { id: 'none', name: 'None', type: 'none' },
  { id: 'cap_backward', name: 'Backward Cap', color: '#1a1a1a' },
  { id: 'beanie_neon', name: 'Neon Beanie', color: '#39FF14' },
  { id: 'fedora', name: 'Fedora', color: '#2d2d2d' },
  { id: 'crown', name: 'Crown', color: '#FFD700', glow: '#FFD700' },
  { id: 'horns', name: 'Devil Horns', color: '#DC2626' },
  { id: 'halo', name: 'Halo', color: '#FFD700', glow: '#FFD700' },
  { id: 'cat_ears', name: 'Cat Ears', color: '#EC4899' },
];

export const FACE_ACCESSORIES = [
  { id: 'none', name: 'None', type: 'none' },
  { id: 'glasses_aviator', name: 'Aviators', color: '#1a1a1a' },
  { id: 'glasses_neon', name: 'Neon Shades', color: '#39FF14', glow: '#39FF14' },
  { id: 'mask_led', name: 'LED Mask', color: '#9333EA', glow: '#9333EA' },
  { id: 'piercing', name: 'Nose Ring', color: '#FFD700' },
  { id: 'cyberpunk', name: 'Cyber Visor', color: '#22D3EE', glow: '#22D3EE' },
  { id: 'bandana', name: 'Bandana', color: '#DC2626' },
  { id: 'face_paint', name: 'UV Paint', color: '#9333EA', glow: '#9333EA' },
];

const AvatarEditor = ({ 
  isOpen, 
  onClose, 
  avatar, 
  updateAvatar, 
  isMobile = false,
  savedOutfits = [],
  onSaveOutfit,
  onLoadOutfit,
  onDeleteOutfit
}) => {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[9990] flex items-center justify-center bg-black/80 p-2 sm:p-4 overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Card className={`w-full ${isMobile ? 'max-w-full max-h-[95vh]' : 'max-w-2xl max-h-[90vh]'} overflow-y-auto bg-gray-900 border-purple-500/30 my-4`}>
        <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-gray-900 z-10">
          <CardTitle className="text-white">Avatar Editor</CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4 sm:space-y-6">
          {/* Gender Selection */}
          <div>
            <Label className="text-white mb-2 block text-sm">Body Type</Label>
            <div className="flex gap-3">
              <button
                onClick={() => updateAvatar('gender', 'male')}
                className={`flex-1 p-3 rounded-lg border-2 transition-all flex flex-col items-center ${
                  avatar.gender === 'male' 
                    ? 'border-purple-500 ring-2 ring-purple-500/50 bg-purple-500/20' 
                    : 'border-white/20 hover:border-white/40'
                }`}
              >
                <span className="text-2xl">👨</span>
                <span className="text-xs text-white/80 mt-1">Male</span>
              </button>
              <button
                onClick={() => updateAvatar('gender', 'female')}
                className={`flex-1 p-3 rounded-lg border-2 transition-all flex flex-col items-center ${
                  avatar.gender === 'female' 
                    ? 'border-pink-500 ring-2 ring-pink-500/50 bg-pink-500/20' 
                    : 'border-white/20 hover:border-white/40'
                }`}
              >
                <span className="text-2xl">👩</span>
                <span className="text-xs text-white/80 mt-1">Female</span>
              </button>
            </div>
          </div>
          
          {/* Skin Tone */}
          <div>
            <Label className="text-white mb-2 block text-sm">Skin Tone</Label>
            <div className="flex gap-2 flex-wrap justify-center">
              {SKIN_TONES.map(tone => (
                <button
                  key={tone.id}
                  onClick={() => updateAvatar('skinTone', tone.id)}
                  className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full border-3 transition-all ${
                    avatar.skinTone === tone.id 
                      ? 'border-white ring-2 ring-purple-500 scale-110' 
                      : 'border-white/30 hover:scale-105 hover:border-white/60'
                  }`}
                  style={{ 
                    backgroundColor: tone.color,
                    boxShadow: avatar.skinTone === tone.id ? '0 0 15px rgba(168, 85, 247, 0.5)' : 'none'
                  }}
                  title={tone.name}
                />
              ))}
            </div>
          </div>

          {/* Sprite Hue (Nightclub character color) */}
          <div data-testid="sprite-hue-section">
            <Label className="text-white mb-2 block text-sm">
              Nightclub Character Tint
              <span className="text-xs text-white/50 ml-2">
                (recolors your in-club sprite)
              </span>
            </Label>
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-md border border-white/30 flex-shrink-0"
                style={{
                  background: `hsl(${avatar.spriteHue || 0}, 70%, 50%)`,
                  boxShadow: `0 0 12px hsl(${avatar.spriteHue || 0}, 80%, 50%, 0.6)`,
                }}
                title={`Hue ${avatar.spriteHue || 0}°`}
              />
              <Slider
                data-testid="sprite-hue-slider"
                value={[avatar.spriteHue || 0]}
                onValueChange={(v) => updateAvatar('spriteHue', v[0])}
                min={0}
                max={359}
                step={1}
                className="flex-1"
              />
              <button
                data-testid="sprite-hue-reset"
                onClick={() => updateAvatar('spriteHue', 0)}
                className="text-xs px-2 py-1 rounded bg-white/10 text-white/70 hover:bg-white/20"
                title="Reset to default"
              >
                Reset
              </button>
            </div>
          </div>
          
          {/* Face Mood */}
          <div>
            <Label className="text-white mb-2 block text-sm">Face Mood</Label>
            <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
              {FACE_MOODS.map(mood => (
                <button
                  key={mood.id}
                  onClick={() => updateAvatar('faceMood', mood.id)}
                  className={`p-2 rounded-lg border-2 transition-all ${
                    avatar.faceMood === mood.id 
                      ? 'border-purple-500 ring-2 ring-purple-500/50' 
                      : 'border-white/20 hover:border-white/40'
                  }`}
                >
                  <span className="text-xl sm:text-2xl block">{mood.emoji}</span>
                  <span className="text-[10px] sm:text-xs text-white/80">{mood.name}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* NEW P2: Hair Style */}
          <div>
            <Label className="text-white mb-2 block text-sm">Hair Style</Label>
            <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
              {HAIR_STYLES.map(style => (
                <button
                  key={style.id}
                  onClick={() => updateAvatar('hairStyle', style.id)}
                  className={`p-2 rounded-lg border-2 transition-all ${
                    avatar.hairStyle === style.id 
                      ? 'border-purple-500 ring-2 ring-purple-500/50' 
                      : 'border-white/20 hover:border-white/40'
                  }`}
                >
                  <div 
                    className="w-6 h-6 sm:w-8 sm:h-8 rounded-full mx-auto mb-0.5 flex items-center justify-center bg-gray-700"
                  >
                    {style.type === 'none' && <span className="text-white/50 text-lg">∅</span>}
                    {style.type === 'short' && <span className="text-white/80 text-lg">💇</span>}
                    {style.type === 'buzz' && <span className="text-white/80 text-lg">👨‍🦲</span>}
                    {style.type === 'mohawk' && <span className="text-white/80 text-lg">🦔</span>}
                    {style.type === 'long' && <span className="text-white/80 text-lg">💁</span>}
                    {style.type === 'ponytail' && <span className="text-white/80 text-lg">🎀</span>}
                    {style.type === 'curly' && <span className="text-white/80 text-lg">🌀</span>}
                    {style.type === 'spiky' && <span className="text-white/80 text-lg">⚡</span>}
                  </div>
                  <span className="text-[10px] sm:text-xs text-white/80 truncate block">{style.name}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* NEW P2: Hair Color - only show if hair style is not none */}
          {avatar.hairStyle && avatar.hairStyle !== 'none' && (
            <div>
              <Label className="text-white mb-2 block text-sm">Hair Color</Label>
              <div className="flex gap-2 flex-wrap justify-center">
                {HAIR_COLORS.map(color => (
                  <button
                    key={color.id}
                    onClick={() => updateAvatar('hairColor', color.id)}
                    className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full border-3 transition-all ${
                      avatar.hairColor === color.id 
                        ? 'border-white ring-2 ring-purple-500 scale-110' 
                        : 'border-white/30 hover:scale-105 hover:border-white/60'
                    }`}
                    style={{ 
                      backgroundColor: color.color,
                      boxShadow: color.glow ? `0 0 15px ${color.glow}` : 
                        (avatar.hairColor === color.id ? '0 0 15px rgba(168, 85, 247, 0.5)' : 'none')
                    }}
                    title={color.name}
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* Eye Glow Color */}
          <div>
            <Label className="text-white mb-2 block text-sm">Eye Glow Color</Label>
            <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
              {EYE_COLORS.map(color => (
                <button
                  key={color.id}
                  onClick={() => updateAvatar('eyeColor', color.id)}
                  className={`p-2 rounded-lg border-2 transition-all ${
                    avatar.eyeColor === color.id 
                      ? 'border-purple-500 ring-2 ring-purple-500/50' 
                      : 'border-white/20 hover:border-white/40'
                  }`}
                >
                  <div 
                    className="w-6 h-6 sm:w-8 sm:h-8 rounded-full mx-auto mb-0.5" 
                    style={{ 
                      backgroundColor: color.color,
                      boxShadow: `0 0 10px ${color.color}`
                    }}
                  />
                  <span className="text-[10px] sm:text-xs text-white/80">{color.name}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Eye Glow Intensity */}
          <div>
            <Label className="text-white mb-2 block text-sm">Eye Glow Intensity: {avatar.eyeGlow || 50}%</Label>
            <Slider 
              value={[avatar.eyeGlow || 50]} 
              onValueChange={(val) => updateAvatar('eyeGlow', val[0])}
              max={100}
              step={10}
              className="w-full"
            />
          </div>
          
          {/* Top */}
          <div>
            <Label className="text-white mb-2 block text-sm flex items-center gap-2">
              <Shirt className="w-4 h-4" /> Top
            </Label>
            <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
              {TOPS.map(top => (
                <button
                  key={top.id}
                  onClick={() => updateAvatar('top', top.id)}
                  className={`p-2 rounded-lg border-2 transition-all ${
                    avatar.top === top.id 
                      ? 'border-purple-500 ring-2 ring-purple-500/50' 
                      : 'border-white/20 hover:border-white/40'
                  }`}
                >
                  <div 
                    className="w-6 h-6 sm:w-8 sm:h-8 rounded mx-auto mb-0.5" 
                    style={{ 
                      backgroundColor: top.color,
                      boxShadow: top.glow ? `0 0 10px ${top.glow}` : 'none'
                    }}
                  />
                  <span className="text-[10px] sm:text-xs text-white/80 truncate block">{top.name}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Pants */}
          <div>
            <Label className="text-white mb-2 block text-sm">Pants</Label>
            <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
              {PANTS.map(pant => (
                <button
                  key={pant.id}
                  onClick={() => updateAvatar('pants', pant.id)}
                  className={`p-2 rounded-lg border-2 transition-all ${
                    avatar.pants === pant.id 
                      ? 'border-purple-500 ring-2 ring-purple-500/50' 
                      : 'border-white/20 hover:border-white/40'
                  }`}
                >
                  <div 
                    className="w-6 h-6 sm:w-8 sm:h-8 rounded mx-auto mb-0.5" 
                    style={{ backgroundColor: pant.color }}
                  />
                  <span className="text-[10px] sm:text-xs text-white/80 truncate block">{pant.name}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Shoes */}
          <div>
            <Label className="text-white mb-2 block text-sm">Shoes</Label>
            <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
              {SHOES.map(shoe => (
                <button
                  key={shoe.id}
                  onClick={() => updateAvatar('shoes', shoe.id)}
                  className={`p-2 rounded-lg border-2 transition-all ${
                    avatar.shoes === shoe.id 
                      ? 'border-purple-500 ring-2 ring-purple-500/50' 
                      : 'border-white/20 hover:border-white/40'
                  }`}
                >
                  <div 
                    className="w-6 h-6 sm:w-8 sm:h-8 rounded mx-auto mb-0.5" 
                    style={{ 
                      backgroundColor: shoe.color === 'transparent' ? '#333' : shoe.color,
                      boxShadow: shoe.glow ? `0 0 10px ${shoe.glow}` : 'none'
                    }}
                  />
                  <span className="text-[10px] sm:text-xs text-white/80 truncate block">{shoe.name}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Hat */}
          <div>
            <Label className="text-white mb-2 block text-sm">Hat/Headwear</Label>
            <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
              {HATS.map(hat => (
                <button
                  key={hat.id}
                  onClick={() => updateAvatar('hat', hat.id)}
                  className={`p-2 rounded-lg border-2 transition-all ${
                    avatar.hat === hat.id 
                      ? 'border-purple-500 ring-2 ring-purple-500/50' 
                      : 'border-white/20 hover:border-white/40'
                  }`}
                >
                  <div 
                    className="w-6 h-6 sm:w-8 sm:h-8 rounded mx-auto mb-0.5 flex items-center justify-center" 
                    style={{ 
                      backgroundColor: hat.color || '#333',
                      boxShadow: hat.glow ? `0 0 10px ${hat.glow}` : 'none'
                    }}
                  >
                    {hat.type === 'none' && <span className="text-white/50 text-lg">∅</span>}
                  </div>
                  <span className="text-[10px] sm:text-xs text-white/80 truncate block">{hat.name}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Face Accessories */}
          <div>
            <Label className="text-white mb-2 block text-sm flex items-center gap-2">
              <Glasses className="w-4 h-4" /> Face Accessories
            </Label>
            <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
              {FACE_ACCESSORIES.map(acc => (
                <button
                  key={acc.id}
                  onClick={() => updateAvatar('accessory', acc.id)}
                  className={`p-2 rounded-lg border-2 transition-all ${
                    avatar.accessory === acc.id 
                      ? 'border-purple-500 ring-2 ring-purple-500/50' 
                      : 'border-white/20 hover:border-white/40'
                  }`}
                >
                  <div 
                    className="w-6 h-6 sm:w-8 sm:h-8 rounded mx-auto mb-0.5 flex items-center justify-center" 
                    style={{ 
                      backgroundColor: acc.color || '#333',
                      boxShadow: acc.glow ? `0 0 10px ${acc.glow}` : 'none'
                    }}
                  >
                    {acc.type === 'none' && <span className="text-white/50 text-lg">∅</span>}
                  </div>
                  <span className="text-[10px] sm:text-xs text-white/80 truncate block">{acc.name}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Saved Outfits */}
          {savedOutfits.length > 0 && (
            <div>
              <Label className="text-white mb-2 block text-sm">Saved Outfits</Label>
              <div className="flex flex-wrap gap-2">
                {savedOutfits.map((outfit, index) => (
                  <div key={index} className="flex items-center gap-1">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => onLoadOutfit(outfit)}
                      className="text-xs"
                    >
                      {outfit.name || `Outfit ${index + 1}`}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => onDeleteOutfit(index)}
                      className="text-red-400 hover:text-red-300 px-1"
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Save Outfit Button */}
          <div className="flex gap-2">
            <Button 
              onClick={onSaveOutfit}
              variant="outline"
              className="flex-1"
            >
              Save Outfit
            </Button>
          </div>
          
          <Button 
            onClick={onClose}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
          >
            Done
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default AvatarEditor;
